import React from 'react';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { versionDetailsRelayResourceCustom } from '@atlassian/jira-router-resources-software-versions/src/controllers/index.tsx';
import {
	useViewVersionDetailsPageExperienceFail,
	VersionDetailsPageExperienceStart,
} from '@atlassian/jira-software-releases-version-detail/src/services/experience-tracker/ViewVersionDetailsPageExperienceSuccessTracker.tsx';
import { VersionDetail as VersionDetailPage } from '@atlassian/jira-software-releases-version-detail/src/ui/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import messages from './messages.tsx';
import { isNetworkError } from './utils.tsx';

export const VersionDetailSpa = () => {
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();

	const projectKey = match.params.projectKey ?? '';
	const { data: projectContext } = useProjectContext();
	const onExperienceFail = useViewVersionDetailsPageExperienceFail();

	const { queryReference } = useRelayResource(versionDetailsRelayResourceCustom);

	const releasesHomeUrl = `/projects/${projectKey}?selectedItem=com.atlassian.jira.jira-projects-plugin%3Arelease-page`;

	// Note: "the no Software license" case is handled by a redirect within `VersionDetailPage`
	return (
		<>
			<VersionDetailsPageExperienceStart experienceId={projectKey} />
			{queryReference === null ? null : (
				<AuthenticationCheck>
					<JSErrorBoundary
						id="pageContent"
						packageName="jiraSpaAppsSoftwareReleasesVersionDetails"
						teamName="fusion-solaris"
						extraEventData={{
							isSimplifiedProject: projectContext?.simplified ?? false,
						}}
						onError={(location: string, error: Error) => {
							onExperienceFail(location, error, {
								isNetworkError: isNetworkError(error),
							});
						}}
						attributes={(error: Error) => ({
							isNetworkError: isNetworkError(error),
							isSimplifiedProject: projectContext?.simplified ?? false,
						})}
					>
						<SubProductUpdater subProduct="software" />
						<ProjectBreadcrumb
							baseUrl=""
							projectKey={projectKey}
							projectName={projectContext?.projectName ?? ''}
						>
							<Breadcrumb
								breadcrumb={{
									text: formatMessage(messages.releases),
									href: releasesHomeUrl,
								}}
							>
								<VersionDetailPage queryReference={queryReference} />
							</Breadcrumb>
						</ProjectBreadcrumb>
					</JSErrorBoundary>
				</AuthenticationCheck>
			)}
		</>
	);
};
